import { makeStyles } from '@material-ui/core';
import { motion } from 'framer-motion';

/**
 * Concrete recipes that combine the three services, so the AI workspace reads
 * as something you build with rather than three unrelated links.
 */
const RECIPES = [
  {
    step: '01',
    title: 'WhatsApp support triage',
    chain: ['Messaging', 'AI Gateway', 'Automation'],
    body: 'Inbound message hits a Messaging webhook, the AI Gateway classifies intent and drafts a reply, Automation routes anything low-confidence to a human queue.',
  },
  {
    step: '02',
    title: 'Catalog-aware changelog',
    chain: ['Automation', 'AI Gateway', 'Quodin'],
    body: 'On merge, Automation pulls the diff, the AI Gateway summarises it in plain language, and the result is posted back to the component page in the catalog.',
  },
  {
    step: '03',
    title: 'Nightly cost digest',
    chain: ['AI Gateway', 'Automation', 'Messaging'],
    body: 'Gateway spend logs roll up per team, Automation formats the digest, Messaging delivers it to the engineering WhatsApp group before standup.',
  },
];

const useStyles = makeStyles({
  list: { display: 'grid', gap: 0 },
  row: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gap: 18,
    padding: '18px 0',
    borderBottom: '1px solid var(--q-border)',
    '&:last-child': { borderBottom: 'none' },
  },
  step: {
    fontFamily: 'var(--q-font-mono)',
    fontSize: 11,
    letterSpacing: '0.12em',
    color: 'var(--q-emerald)',
    paddingTop: 3,
  },
  title: {
    fontFamily: 'var(--q-font-display)',
    fontWeight: 700,
    fontSize: 15.5,
    letterSpacing: '-0.01em',
    color: 'var(--q-text)',
    margin: 0,
  },
  body: {
    color: 'var(--q-sub)',
    fontSize: 13.5,
    lineHeight: 1.6,
    margin: '6px 0 0',
    maxWidth: '68ch',
  },
  chain: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    flexWrap: 'wrap',
    marginTop: 10,
  },
  hop: {
    fontFamily: 'var(--q-font-mono)',
    fontSize: 10,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--q-muted)',
    padding: '3px 8px',
    borderRadius: 5,
    border: '1px solid var(--q-border)',
    background: 'rgba(255,255,255,0.02)',
  },
  arrow: { color: 'var(--q-slate)', fontSize: 11, lineHeight: 1 },
});

export const WorkflowStarters = () => {
  const classes = useStyles();
  return (
    <div className={classes.list}>
      {RECIPES.map((r, i) => (
        <motion.div
          key={r.step}
          className={classes.row}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-30px' }}
          transition={{
            delay: i * 0.07,
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <span className={classes.step}>{r.step}</span>
          <div>
            <h3 className={classes.title}>{r.title}</h3>
            <p className={classes.body}>{r.body}</p>
            <div className={classes.chain}>
              {r.chain.map((hop, idx) => (
                <span key={hop} style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
                  <span className={classes.hop}>{hop}</span>
                  {idx < r.chain.length - 1 && (
                    <span className={classes.arrow} aria-hidden>
                      →
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};
